import { Component } from '@theme/component';

const INTERVAL_MS = 20 * 60 * 1000;
const DEFAULT_VARIANCE = 15;

/**
 * @typedef {Object} ProductSocialProofRefs
 * @property {HTMLElement} count
 */

/**
 * Rotates the social-proof purchase count every 20 minutes.
 * @extends {Component<ProductSocialProofRefs>}
 */
class ProductSocialProof extends Component {
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#renderCount();
    this.#scheduleNextUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer != null) clearTimeout(this.#timer);
  }

  get #baseCount() {
    const value = Number(this.dataset.baseCount);
    return Number.isFinite(value) && value > 0 ? Math.round(value) : 47;
  }

  get #variance() {
    const value = Number(this.dataset.variance);
    return Number.isFinite(value) && value >= 0 ? Math.round(value) : DEFAULT_VARIANCE;
  }

  get #template() {
    return this.dataset.template || '[count] bought in the last 7 days';
  }

  get #seedKey() {
    return this.dataset.productId || this.dataset.blockId || 'product';
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #renderCount() {
    const countEl = this.refs.count;
    if (!(countEl instanceof HTMLElement)) return;

    const count = this.#getCountForCurrentWindow();
    const text = this.#escapeHtml(this.#template.replace(/\[count\]/g, String(count)));

    // Bold "[count] bought" and "7 days" (or similar "N day(s)")
    countEl.innerHTML = text
      .replace(/^(\d+)\s+(bought)/i, '<strong class="product-social-proof__emphasis">$1 $2</strong>')
      .replace(/(\d+)\s+(days?)/gi, '<strong class="product-social-proof__emphasis">$1 $2</strong>');
  }

  #getCountForCurrentWindow() {
    const bucket = Math.floor(Date.now() / INTERVAL_MS);
    const min = Math.max(1, this.#baseCount - this.#variance);
    const max = Math.min(99, this.#baseCount + this.#variance);
    const span = Math.max(0, max - min);
    const offset = this.#hash(`${this.#seedKey}:${bucket}`) % (span + 1);
    return min + offset;
  }

  /**
   * Deterministic 32-bit hash for stable counts within each 20-minute window.
   * @param {string} input
   */
  #hash(input) {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i++) {
      hash ^= input.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  #scheduleNextUpdate() {
    if (this.#timer != null) clearTimeout(this.#timer);

    const now = Date.now();
    const delay = INTERVAL_MS - (now % INTERVAL_MS) + 50;

    this.#timer = setTimeout(() => {
      this.#renderCount();
      this.#scheduleNextUpdate();
    }, delay);
  }
}

if (!customElements.get('product-social-proof')) {
  customElements.define('product-social-proof', ProductSocialProof);
}
